// Vite entry point for main SPA

// Styles
import '@styles/application.css.scss'

// Third-party libraries
import { createApp } from 'vue'

// i18n (must be before components that use $t, also loads moment locales)
import { fetchAndInitI18n } from './helpers/i18n.js'

// Utilities
import PageSync from './helpers/page-sync.js'
import PageSyncFayeAdapter from './helpers/page-sync-faye-adapter.js'
import VersionSync from './helpers/version-sync.js'
import { isDevEnv, isTestEnv } from './helpers/env.js'

// Store (must be created before modules)
import store from './store.js'

// Store modules are lazy-loaded by components that use them


// Plugins
import confirmPlugin from './plugins/confirm.js'

// Directives
import tooltipDirective from './directives/tooltip.js'
import popoverDirective from './directives/popover.js'
import dropdownDirective from './directives/dropdown.js'

// Filter
import formatDate from './filters/format_date.js'

// Popover positioning utility
import './helpers/popover-position.js'

// Router (must be after store and components)
import router from './router.js'

// App layout component
import App from './layouts/app.vue'

// Initialize PageSync
const pageSyncLink = document.querySelector('link[rel="page-sync"]')
if (pageSyncLink) {
  const pageSyncUrl = pageSyncLink.href.replace(/^https?:\/\/[^/:]+/, `${location.protocol}//${location.hostname}`)
  PageSync.logging = isDevEnv() || isTestEnv()
  PageSync.init(PageSyncFayeAdapter, pageSyncUrl)
}

// Initialize VersionSync for auto-update detection
VersionSync.init()

// Initialize Vue SPA
async function initApp() {
  if (!document.getElementById('app')) return

  // Fetch translations and initialize i18n
  const i18n = await fetchAndInitI18n()

  // Create Vue 3 app instance
  const app = createApp(App)

  // Register plugins
  app.use(router)
  app.use(store)
  app.use(i18n)
  app.use(confirmPlugin)

  // Register directives
  app.directive('tooltip', tooltipDirective)
  app.directive('popover', popoverDirective)
  app.directive('dropdown', dropdownDirective)

  // Register filter as global property
  app.config.globalProperties.$filters = {
    formatDate
  }

  // Mount
  window.VueApp = app.mount('#app')
}

// ES modules are deferred, so DOMContentLoaded may have already fired
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initApp)
} else {
  initApp()
}
